import React from 'react';
import { ArrowRight, Sparkles, MapPin, Clock, Check } from 'lucide-react';
import { Button } from './ui/button';
import { businessInfo } from '../mock';

const Hero = () => {
  const scrollToBooking = () => {
    const element = document.getElementById('book-now');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative pt-24 pb-16 sm:pt-32 sm:pb-24 overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50">
        <div className="absolute top-0 right-0 w-96 h-96 bg-teal-200 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-200 rounded-full opacity-20 blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left content */}
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-medium">
              <Sparkles size={16} />
              <span>Professional Laundry Service</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              🧺 Wash & Fold
              <span className="block text-teal-600">Pickup & Delivery</span>
            </h1>

            {/* Main pricing highlight */}
            <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-teal-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <div className="text-4xl font-bold text-teal-600">$1.50/lb</div>
                  <div className="text-lg text-gray-600">$15 minimum order</div>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-gray-700">
                    <Check size={18} className="text-teal-500" />
                    <span>FREE Pickup & Delivery</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <Check size={18} className="text-teal-500" />
                    <span>Same-Day Service Available</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-lg sm:text-xl text-gray-700 font-medium flex items-center gap-2">
                <MapPin size={20} className="text-teal-600" />
                Serving Snellville • Lilburn • Lawrenceville • Norcross
              </p>
            </div>

            {/* Hours info - Clear distinction */}
            <div className="bg-white/80 rounded-xl p-4 border border-gray-200 space-y-3">
              <div className="flex items-start gap-3">
                <Clock size={20} className="text-teal-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-gray-800">{businessInfo.laundromat_hours}</div>
                </div>
              </div>
              <div className="border-t pt-3">
                <div className="font-semibold text-gray-800">Wash & Fold Pickup & Delivery</div>
                <div className="text-gray-700">Pickup Hours: 9:00 AM – 4:00 PM</div>
                <p className="text-sm text-gray-500 mt-1">
                  Same-day service available for qualifying orders picked up before 4:00 PM
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={scrollToBooking}
                size="lg" 
                className="bg-teal-600 hover:bg-teal-700 text-white text-lg px-8 py-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                Book Your Free Pickup
                <ArrowRight className="ml-2" size={20} />
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                onClick={() => window.location.href = `tel:${businessInfo.phone}`}
                className="border-2 border-teal-600 text-teal-600 hover:bg-teal-50 text-lg px-8 py-6 rounded-xl transition-all duration-300"
              >
                Call Now
              </Button>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap gap-8 pt-8 border-t border-gray-200">
              <div>
                <div className="text-3xl font-bold text-teal-600">24/7</div>
                <div className="text-sm text-gray-600">Laundromat Open</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-teal-600">4</div>
                <div className="text-sm text-gray-600">Cities Served</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-teal-600">Same Day</div>
                <div className="text-sm text-gray-600">Delivery Available</div>
              </div>
            </div>
          </div>

          {/* Right content - Image */}
          <div className="relative lg:h-[600px]">
            <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-3xl transform rotate-3"></div>
            <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden transform -rotate-2 hover:rotate-0 transition-transform duration-500">
              <img 
                src="https://images.unsplash.com/photo-1604176354204-9268737828e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxmb2xkZWQlMjBjbG90aGVzfGVufDB8fHx8MTc2NjYwMDQ2NHww&ixlib=rb-4.1.0&q=85"
                alt="Fresh folded laundry"
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Floating badge */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6 border-2 border-teal-100">
              <div className="text-center">
                <div className="text-4xl font-bold text-teal-600">11th</div>
                <div className="text-sm font-medium text-gray-600">Service FREE*</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
